<script>
  import { onDestroy } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Badge, Button, Card, CardBody, CardHeader, Collapse, Progress, Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';

  import { streamingManager } from '@client/stores/streaming-manager';

  let collapsed = false;
  let streams = new Map();

  const statusColorMap = {
    running: 'primary',
    completed: 'success',
    failed: 'danger',
    cancelled: 'secondary'
  };

  const statusIconMap = {
    pending: 'clock',
    running: 'loader',
    completed: 'check-circle',
    failed: 'x-circle',
    cancelled: 'x'
  };

  const getStatusColor = (status) => {
    return statusColorMap[status] || 'light';
  };

  const getStatusIcon = (status) => {
    return statusIconMap[status] || 'help-circle';
  };

  const getProgressPercentage = (stream) => {
    if (!stream?.progress?.total || !stream?.progress?.current) return 0;
    return Math.ceil((stream.progress.current / stream.progress.total) * 100);
  };

  const formatDuration = (startedAt, completedAt) => {
    try {
      if (!startedAt) return '0s';
      const end = completedAt || Date.now();
      const duration = Math.floor((end - startedAt) / 1000);

      if (duration < 60) return `${duration}s`;
      const minutes = Math.floor(duration / 60);
      const seconds = duration % 60;
      return `${minutes}m ${seconds}s`;
    } catch (error) {
      console.warn('Error formatting duration:', error);
      return '0s';
    }
  };

  const handleCancel = (streamId) => {
    streamingManager.cancelStream(streamId);
  };

  const handleRemove = (streamId) => {
    streamingManager.removeStream(streamId);
  };

  const handleClearCompleted = () => {
    streamingManager.clearCompleted();
  };

  const handleViewResults = (streamId) => {
    const stream = streams.get(streamId);

    if (stream) {
      streamingManager.updateStream(streamId, {
        expanded: !stream.expanded
      });
    }
  };

  const handleDownloadResults = (streamId) => {
    try {
      const stream = streams.get(streamId);

      if (!stream?.results?.length) {
        return;
      }

      const safeTitle = (stream.title || 'export').replace(/[^a-zA-Z0-9]/g, '_');
      const headers = ['Name', 'Status', 'Timestamp'];

      const csvContent = [
        headers.join(','),
        ...stream.results.map((result) => {
          const name = (result?.name || 'Unknown').replace(/"/g, '""');
          const status = result?.success ? 'Success' : 'Failed';
          let timestamp = 'Unknown';

          try {
            timestamp = result?.timestamp ? new Date(result.timestamp).toISOString() : 'Unknown';
          } catch (e) {
            console.warn('Invalid timestamp:', result?.timestamp);
          }

          return [`"${name}"`, status, timestamp].join(',');
        })
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `${safeTitle}_results.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading results:', error);
    }
  };

  const toggleCollapse = () => {
    collapsed = !collapsed;
  };

  let unsubscribe = null;

  try {
    unsubscribe = streamingManager.subscribe((state) => {
      if (state?.streams) {
        streams = state.streams;
      }
    });
  } catch (error) {
    console.error('Error subscribing to streaming manager:', error);
  }

  onDestroy(() => {
    try {
      if (unsubscribe) {
        unsubscribe();
      }
    } catch (error) {
      console.error('Error unsubscribing:', error);
    }
  });

  $: activeStreams = Array.from(streams?.values() || []).filter(
    (stream) => stream.status && !['completed', 'failed', 'cancelled'].includes(stream.status)
  );

  $: completedStreams = Array.from(streams?.values() || []).filter(
    (stream) => stream.status && ['completed', 'failed', 'cancelled'].includes(stream.status)
  );

  $: hasStreams = streams?.size > 0;
  $: hasActive = activeStreams.length > 0;
</script>

{#if hasStreams}
  <div class="activity-manager shadow-sm">
    <Card>
      <CardHeader class="d-flex justify-content-between align-items-center border-bottom">
        <div class="d-flex align-items-center">
          <span class="bold">{$format('label.ACTIVITY_MANAGER')}</span>
          {#if hasActive}
            <Badge color="primary" class="ms-2 rounded-circle">{activeStreams.length}</Badge>
          {/if}
        </div>
        <div class="d-flex align-items-center">
          {#if completedStreams.length > 0}
            <Button size="sm" color="link" class="p-0 me-2" on:click={handleClearCompleted}>
              <Icon name="x" />
            </Button>
          {/if}
          <Button size="sm" color="link" class="p-0" on:click={toggleCollapse}>
            <Icon name={collapsed ? 'chevron-up' : 'chevron-down'} />
          </Button>
        </div>
      </CardHeader>

      <Collapse isOpen={!collapsed}>
        <CardBody class="p-3">
          <div class="stream-list">
            {#each [...[...activeStreams].reverse(), ...[...completedStreams].reverse()] as stream (stream.id)}
              <div class="stream-item mb-2">
                <div class="d-flex justify-content-between align-items-start">
                  <div class="flex-grow-1">
                    <div class="d-flex align-items-center mb-1">
                      <Icon
                        name={getStatusIcon(stream.status)}
                        class="me-2 {stream.status === 'running' ? 'spinning' : ''}"
                      />
                      <span class="stream-title text-capitalize">{stream?.title || 'Unnamed Stream'}</span>
                      <Badge color={getStatusColor(stream.status)} class="ms-2 p-1 rounded" style="font-size: 11px;">
                        {$format(`label.${(stream.status || 'unknown').toUpperCase()}`)}
                        {#if stream.status === 'completed' && stream.progress?.successes > 0}
                          ({stream.progress.successes}/{stream.progress.successes + (stream.progress.failures || 0)})
                        {/if}
                      </Badge>
                    </div>

                    {#if stream.status === 'running'}
                      <div class="progress-container mb-1">
                        {#if stream.progress?.total}
                          <Progress value={getProgressPercentage(stream)} class="mb-1" />
                          <small class="text-muted">
                            {stream.progress?.current || 0} / {stream.progress?.total || 0}
                            ({stream.progress?.successes || 0}
                            {$format('label.SUCCESS.PUSH_STATUS')},
                            {stream.progress?.failures || 0}
                            {$format('label.FAILED')})
                          </small>
                        {:else}
                          <Progress animated striped value={100} class="mb-1" />
                          <small class="text-muted">
                            {stream.progress?.current || 0}
                            {$format('label.PROCESSED')}
                            ({stream.progress?.successes || 0}
                            {$format('label.SUCCESS.PUSH_STATUS')},
                            {stream.progress?.failures || 0}
                            {$format('label.FAILED')})
                          </small>
                        {/if}
                      </div>
                    {/if}

                    {#if stream.error}
                      <div class="text-danger small">{stream.error}</div>
                    {/if}

                    {#if stream.status !== 'running'}
                      <div class="stream-meta small text-muted ms-4">
                        {$format('label.DURATION')}: {formatDuration(stream.startedAt, stream.completedAt)} •
                        {new Date(stream.startedAt)?.toLocaleTimeString() || 'Unknown time'}
                      </div>
                    {/if}
                  </div>

                  <div class="stream-actions ms-2">
                    {#if stream.status !== 'running' && stream.results?.length > 0}
                      <Button
                        id="view-{stream.id}"
                        size="sm"
                        color="outline"
                        class="me-1"
                        on:click={() => handleViewResults(stream.id)}
                      >
                        <Icon name={stream.expanded ? 'chevron-up' : 'eye'} />
                      </Button>
                      <Tooltip target="view-{stream.id}" placement="top">
                        {stream.expanded ? $format('label.HIDE_RESULTS') : $format('label.REVIEW_RESULTS')}
                      </Tooltip>

                      <Button
                        id="download-{stream.id}"
                        size="sm"
                        color="outline"
                        class="me-1"
                        on:click={() => handleDownloadResults(stream.id)}
                      >
                        <Icon name="download" />
                      </Button>
                      <Tooltip target="download-{stream.id}" placement="top">
                        {$format('label.DOWNLOAD_RESULTS')}
                      </Tooltip>
                    {/if}
                    {#if stream.status === 'running'}
                      <Button
                        id="cancel-{stream.id}"
                        size="sm"
                        color="outline"
                        on:click={() => handleCancel(stream.id)}
                      >
                        <Icon name="x" />
                      </Button>
                      <Tooltip target="cancel-{stream.id}" placement="top">
                        {$format('label.CANCEL')}
                      </Tooltip>
                    {:else}
                      <Button
                        id="delete-{stream.id}"
                        size="sm"
                        color="outline"
                        on:click={() => handleRemove(stream.id)}
                      >
                        <Icon name="trash-2" />
                      </Button>
                      <Tooltip target="delete-{stream.id}" placement="top">
                        {$format('label.DELETE')}
                      </Tooltip>
                    {/if}
                  </div>
                </div>

                {#if stream.expanded && stream.results?.length > 0}
                  <div class="stream-results mt-3 p-3 border-top">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <div class="small bold">{$format('label.SYNC_RESULTS')}</div>
                      <div class="small text-muted">
                        <span class="text-success"
                          >{stream.progress?.successes || 0} {$format('label.SUCCESS.PUSH_STATUS')}</span
                        >
                        /
                        <span class="text-danger">{stream.progress?.failures || 0} {$format('label.FAILED')}</span> /
                        <span>{stream.results?.length || 0} total</span>
                      </div>
                    </div>
                    <div class="results-list">
                      {#each stream.results || [] as result}
                        <div class="result-item d-flex align-items-center small">
                          <Icon
                            name={result.success ? 'check' : 'x'}
                            class="me-2 {result.success ? 'text-success' : 'text-danger'}"
                            size="10"
                          />
                          <span class="text-truncate">{result.name || 'Unknown'}</span>
                        </div>
                      {/each}
                    </div>
                  </div>
                {/if}
              </div>
            {/each}
          </div>
        </CardBody>
      </Collapse>
    </Card>
  </div>
{/if}

<style lang="scss" src="./activity-manager.scss"></style>
